// Puerto verbatim de packages/hce/src/hce/QuestionnaireEngine/responseUtils.ts
// (Módulo H, Grupo 1) — helpers de lectura de QuestionnaireResponse que
// recorren item[] (incluidos los grupos anidados) e indexan por linkId,
// para que extractors.ts no repita la recursión cada vez. Cero dependencias
// de UI — solo tipos de @medplum/fhirtypes.
import type { QuestionnaireResponse, QuestionnaireResponseItem, QuestionnaireResponseItemAnswer } from '@medplum/fhirtypes';

type RespuestaValor = string | boolean | number;

function valorDe(answer: QuestionnaireResponseItemAnswer): RespuestaValor | undefined {
  return (
    answer.valueString ??
    answer.valueBoolean ??
    answer.valueInteger ??
    answer.valueDecimal ??
    answer.valueDate ??
    answer.valueDateTime ??
    answer.valueCoding?.display ??
    answer.valueCoding?.code
  );
}

function recorrer(items: QuestionnaireResponseItem[] | undefined, out: Map<string, RespuestaValor[]>): void {
  for (const item of items ?? []) {
    if (item.answer?.length) {
      const valores = item.answer.map(valorDe).filter((v): v is RespuestaValor => v !== undefined);
      if (valores.length) {
        out.set(item.linkId, [...(out.get(item.linkId) ?? []), ...valores]);
      }
    }
    if (item.item) {
      recorrer(item.item, out);
    }
  }
}

/**
 * Indexa un array de item[] por linkId (recursivo, incluye grupos) — la
 * pieza reusable detrás de `indexarRespuestas`. Se expone aparte para
 * grupos repetibles (`repeats: true` sobre un `type: group`, ej. el
 * odontograma de 033): cada repetición es un `QuestionnaireResponseItem`
 * hermano con el MISMO linkId, así que indexar el QuestionnaireResponse
 * completo de una sola vez mezclaría las respuestas de todas las
 * repeticiones entre sí — hay que indexar cada instancia por separado con
 * esta función, filtrando `response.item` por linkId primero.
 */
export function indexarItems(items: QuestionnaireResponseItem[] | undefined): Map<string, RespuestaValor[]> {
  const out = new Map<string, RespuestaValor[]>();
  recorrer(items, out);
  return out;
}

/** Indexa todas las respuestas del QuestionnaireResponse por linkId (recursivo, incluye grupos). */
export function indexarRespuestas(response: QuestionnaireResponse): Map<string, RespuestaValor[]> {
  return indexarItems(response.item);
}

/** Primera respuesta de un linkId como texto, o undefined si no se contestó. */
export function textoDe(respuestas: Map<string, RespuestaValor[]>, linkId: string): string | undefined {
  const v = respuestas.get(linkId)?.[0];
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

/** Todas las respuestas de un linkId repetible (repeats: true), como texto. */
export function textosDe(respuestas: Map<string, RespuestaValor[]>, linkId: string): string[] {
  return (respuestas.get(linkId) ?? []).filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
}

function recorrerRespuestasCrudas(items: QuestionnaireResponseItem[] | undefined, linkId: string, out: QuestionnaireResponseItemAnswer[]): void {
  for (const item of items ?? []) {
    if (item.linkId === linkId) out.push(...(item.answer ?? []));
    if (item.item) recorrerRespuestasCrudas(item.item, linkId, out);
  }
}

/**
 * Respuestas SIN aplanar de un linkId (recursivo, incluye grupos) — a
 * diferencia de `textoDe`/`textosDe`, no colapsa `valueCoding` a un string:
 * hace falta el objeto completo (system/code/display) para construir
 * `Condition.code.coding` cuando el item es `answerValueSet`-backed. Un item
 * `open-choice` puede responder con `valueCoding` (eligió una opción del
 * catálogo) O `valueString` (texto libre que no matcheó ninguna) — el
 * llamador decide qué hacer con cada caso.
 */
export function respuestasCrudasDe(response: QuestionnaireResponse, linkId: string): QuestionnaireResponseItemAnswer[] {
  const out: QuestionnaireResponseItemAnswer[] = [];
  recorrerRespuestasCrudas(response.item, linkId, out);
  return out;
}

/** Respuesta boolean de un linkId — false si no se contestó (no undefined), porque en un boolean "sin marcar" MSP lo trata como "no"). */
export function booleanoDe(respuestas: Map<string, RespuestaValor[]>, linkId: string): boolean {
  return respuestas.get(linkId)?.[0] === true;
}

/** Primera respuesta de un linkId numérico (integer/decimal), o undefined si no se contestó. */
export function numeroDe(respuestas: Map<string, RespuestaValor[]>, linkId: string): number | undefined {
  const v = respuestas.get(linkId)?.[0];
  return typeof v === 'number' ? v : undefined;
}
